"use client";

import { useEffect } from "react";
import { useSelectedItem } from "@/features/items/hooks/useSelectedItem";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { useAnalytics } from "./useAnalytics";

/**
 * Report which POI the visitor is looking at.
 *
 * Opening a POI is what this app is *for*, and it doesn't change the pathname —
 * the selection lives in the query string — so without this the analytics would
 * show one page view per session and nothing else. That is why a page view is
 * sent here rather than only on navigation.
 *
 * Keyed on the POI's name rather than on the item object: the name is what gets
 * reported, so a hit fires when what would be sent changes rather than whenever
 * the query hands back an object. react-query's structural sharing does usually
 * keep that reference stable across refetches — but "usually" is not something to
 * base a customer's visitor figures on.
 */
export function useTrackSelectedItem() {
  const configs = useProjectConfigs();
  const { data: item } = useSelectedItem();
  const { pageView, event, setDimensions } = useAnalytics();

  const titleField = configs.poiTitleField;
  const rawTitle = titleField ? item?.properties?.[titleField] : undefined;
  const name = item ? (typeof rawTitle === "string" ? rawTitle : item.id) : null;

  useEffect(() => {
    // Cleared on deselection, so later hits aren't still attributed to a POI the
    // visitor has closed.
    setDimensions({ dimension3: name ?? "" });
    pageView();

    if (name) event({ category: "POI", action: "select", label: name });
  }, [name, pageView, event, setDimensions]);
}
